import { useEffect, useState } from "react";

import { SELECTED_ALL_PREFIX } from "../constants";
import type { IAppsCategoryItemDto } from "../types/descriptionDto";

type AppsCategoryDto = {
    categories: readonly IAppsCategoryItemDto[];
};

type HookParams<F, O extends AppsCategoryDto> = {
    options: O;
    values: F;
};

type HookValue = {
    selectedAllCategory: Record<string, boolean>;
    setSelectedAllCategory: (allCategory: Record<string, boolean>) => void;
    initSelectedAllCategory: () => void;
    groupedSubCategories: Record<string, readonly string[]>;
};

type GroupedFilterOptions = {
    selectedAllCategory: Record<string, boolean>;
    subCategories: Record<string, readonly string[]>;
};
const useManageAllCategories = <F extends { values: readonly string[] } | undefined, O extends AppsCategoryDto>({
    options,
    values,
}: HookParams<F, O>): HookValue => {
    const [selectedAllCategory, setSelectedAllCategory] = useState<Record<string, boolean>>({});
    const [groupedSubCategories, setGroupedSubCategories] = useState<Record<string, readonly string[]>>({});

    const initSelectedAllCategory = () => {
        const groupedFilterOptions: GroupedFilterOptions = options.categories.reduce(
            (acc: GroupedFilterOptions, option: IAppsCategoryItemDto) => {
                const selectedAllCategoryName = `${SELECTED_ALL_PREFIX} ${option.name.trim().toLowerCase()}`;
                acc.selectedAllCategory[selectedAllCategoryName] = false;
                acc.subCategories[selectedAllCategoryName] = Array.from(new Set(option.subCategories));
                return acc;
            },
            {
                selectedAllCategory: {},
                subCategories: {},
            },
        );

        setSelectedAllCategory(groupedFilterOptions.selectedAllCategory);
        setGroupedSubCategories(groupedFilterOptions.subCategories);
    };

    useEffect(() => {
        setSelectedAllCategory((prevSelected) => {
            const updatedSelection = { ...prevSelected };
            let hasChanged = false;

            Object.entries(updatedSelection).forEach(([key, isSelected]: [string, boolean]) => {
                if (isSelected) {
                    const categoryValues = values?.values || [];
                    const subCats = groupedSubCategories[key] || [];
                    const isAllSelected = subCats.every((item) => categoryValues.includes(item));
                    if (!isAllSelected) {
                        updatedSelection[key] = false;
                        hasChanged = true;
                    }
                }
            });

            return hasChanged ? updatedSelection : prevSelected;
        });
    }, [values, groupedSubCategories]);

    useEffect(() => {
        initSelectedAllCategory();
    }, [options]);

    return {
        selectedAllCategory,
        setSelectedAllCategory,
        groupedSubCategories,
        initSelectedAllCategory,
    };
};

export default useManageAllCategories;
